// ============================================================================
// DOCUMENT OUTPUT DIALOG (ADR-0062 §6) — Print and Email for a voucher.
// ============================================================================
// Print: pick the copies (the country pack's captions), then open the PDF in a new
// tab to print, or download it. Email: confirm To / CC, then Open in mail app (.eml
// draft with the PDF attached), Share (Web Share API, where the browser can share
// files) or Download. Every output is recorded in the print log by the server; a
// share is reported back after the share sheet completes.

import React, { useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import { printingService, type PrintedFile } from 'api/services';
import type { PrintOptionsDto } from 'api/types';

export type DocumentOutputMode = 'print' | 'email';

interface DocumentOutputDialogProps {
  /** The sales invoice to output; null closes the dialog. */
  documentId: string | null;
  documentLabel?: string;
  mode: DocumentOutputMode;
  onClose: () => void;
}

const saveFile = ({ blob, fileName }: PrintedFile) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
};

const canShareFiles = (): boolean => {
  try {
    const probe = new File([new Blob(['x'], { type: 'application/pdf' })], 'probe.pdf', { type: 'application/pdf' });
    return typeof navigator !== 'undefined' && !!navigator.canShare && navigator.canShare({ files: [probe] });
  } catch {
    return false;
  }
};

const KIND_LABEL: Record<string, string> = {
  PDF: 'Downloaded', PRINT: 'Printed', MAIL_DRAFT: 'Mail draft', SHARE: 'Shared', EMAILED: 'Emailed',
};

export const DocumentOutputDialog: React.FC<DocumentOutputDialogProps> = ({ documentId, documentLabel, mode, onClose }) => {
  const [options, setOptions] = useState<PrintOptionsDto | null>(null);
  const [copies, setCopies] = useState<string[]>([]);
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const shareSupported = useMemo(canShareFiles, []);

  useEffect(() => {
    if (!documentId) { setOptions(null); return; }
    let cancelled = false;
    printingService.getSalesInvoiceOptions(documentId)
      .then(o => {
        if (cancelled) return;
        setOptions(o);
        // A mail carries the recipient's copy; a print starts from the profile's defaults.
        setCopies(mode === 'email' ? [o.copies[0]?.code].filter(Boolean) as string[] : o.defaultCopies);
        setTo(o.mail.to ?? '');
        setCc(o.mail.cc ?? '');
      })
      .catch((err: any) => {
        notificationService.toast.error(err?.response?.data?.message || 'Could not load the print options.');
        onClose();
      });
    return () => { cancelled = true; };
  }, [documentId, mode, onClose]);

  if (!documentId) return null;

  const toggleCopy = (code: string) =>
    setCopies(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);

  const run = async (key: string, action: () => Promise<void>) => {
    if (copies.length === 0) { notificationService.toast.warning('Pick at least one copy.'); return; }
    setBusy(key);
    try {
      await action();
    } catch (err: any) {
      // A blob error body has to be read before its message can be shown.
      let message = 'Could not create the document.';
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try { message = JSON.parse(await data.text())?.message || message; } catch { /* keep the default */ }
      } else if (data?.message) {
        message = data.message;
      }
      if (err?.name !== 'AbortError') notificationService.toast.error(message);
    } finally {
      setBusy(null);
    }
  };

  const openToPrint = () => run('print', async () => {
    // Open the tab first, inside the click, so the browser does not block it as a pop-up.
    const tab = window.open('', '_blank');
    const file = await printingService.getSalesInvoicePdf(documentId, copies, true);
    const url = URL.createObjectURL(file.blob);
    if (tab) tab.location.href = url; else saveFile(file);
    onClose();
  });

  const download = () => run('download', async () => {
    saveFile(await printingService.getSalesInvoicePdf(documentId, copies, false));
    onClose();
  });

  const openInMailApp = () => run('mail', async () => {
    saveFile(await printingService.getSalesInvoiceMailDraft(documentId, to, cc, copies));
    notificationService.toast.info('Open the downloaded .eml file — Outlook opens it as a draft with the invoice attached.');
    onClose();
  });

  const share = () => run('share', async () => {
    const pdf = await printingService.getSalesInvoicePdf(documentId, copies, false);
    const file = new File([pdf.blob], options?.mail.fileName || pdf.fileName, { type: 'application/pdf' });
    await navigator.share({ files: [file], title: options?.mail.subject, text: options?.mail.body });
    await printingService.logSalesInvoiceShare(documentId, copies);
    onClose();
  });

  const title = mode === 'print' ? 'Print' : 'Email';
  return (
    <>
      <div className="modal-backdrop" onClick={busy ? undefined : onClose} />
      <div className="modal-container modal-md" role="dialog" aria-modal="true">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <div>
            <h2 className="modal-title">{title}</h2>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
              {options?.documentNumber || documentLabel}
              {options && options.printCount > 0 && ` · printed ${options.printCount} time${options.printCount === 1 ? '' : 's'}`}
            </div>
          </div>
          <button className="modal-close" onClick={onClose} disabled={!!busy}><IconSystem.x size={20} /></button>
        </div>

        <div className="modal-content" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {!options ? (
            <div style={{ color: 'var(--text-secondary)' }}>Loading…</div>
          ) : (
            <>
              {mode === 'email' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0.6rem' }}>
                  <div className="form-group">
                    <label className="form-label">To</label>
                    <input className="form-input" value={to} onChange={e => setTo(e.target.value)} placeholder="customer@example.com" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">CC</label>
                    <input className="form-input" value={cc} onChange={e => setCc(e.target.value)} placeholder="Separate addresses with commas" />
                  </div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                    <strong>Subject:</strong> {options.mail.subject}
                  </div>
                </div>
              )}

              <div>
                <div className="form-label" style={{ marginBottom: '0.35rem' }}>Copies</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                  {options.copies.map(c => (
                    <label key={c.code} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem' }}>
                      <input type="checkbox" checked={copies.includes(c.code)} onChange={() => toggleCopy(c.code)} />
                      {c.caption}
                    </label>
                  ))}
                </div>
              </div>

              {mode === 'email' && (
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  <strong>Open in mail app</strong> downloads a draft (.eml) with the PDF attached. Outlook on Windows opens it
                  ready to edit and send; other mail apps may show it as a received message — use Share or Download there.
                </div>
              )}

              {options.history.length > 0 && (
                <details>
                  <summary style={{ cursor: 'pointer', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>History</summary>
                  <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.1rem', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                    {options.history.map((h, i) => (
                      <li key={i}>
                        {KIND_LABEL[h.outputKind] ?? h.outputKind} · {new Date(h.createdAt).toLocaleString()} · {h.createdByName || '—'}
                        {h.recipient ? ` · to ${h.recipient}` : ''}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto', gap: '0.5rem' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={!!busy}>Cancel</button>
          <button className="btn btn-secondary" onClick={download} disabled={!options || !!busy}>
            <IconSystem.download size={16} /> {busy === 'download' ? 'Preparing…' : 'Download PDF'}
          </button>
          {mode === 'print' && (
            <button className="btn btn-primary" onClick={openToPrint} disabled={!options || !!busy}>
              <IconSystem.print size={16} /> {busy === 'print' ? 'Preparing…' : 'Open to print'}
            </button>
          )}
          {mode === 'email' && shareSupported && (
            <button className="btn btn-secondary" onClick={share} disabled={!options || !!busy}>
              {busy === 'share' ? 'Preparing…' : 'Share'}
            </button>
          )}
          {mode === 'email' && (
            <button className="btn btn-primary" onClick={openInMailApp} disabled={!options || !!busy || !to.trim()}>
              <IconSystem.mail size={16} /> {busy === 'mail' ? 'Preparing…' : 'Open in mail app'}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default DocumentOutputDialog;
